/**
 * Map the LLM's SEMANTIC theme onto the widget's existing `--bhjs-*` custom
 * properties (the same suffix → CSS-value map the theme editor saves to
 * `website_settings[widget_theme]`). This is the bridge that lets a generated
 * design plug straight into the current widget theming with NO runtime change:
 * the model reasons in human terms (primary / accent / on-surface), the code
 * deterministically translates to the widget's real variables.
 *
 * Emits only keys in THEME_VAR_KEYS (./theme-fields), which is the same
 * allow-list dashboard-api validates writes against — so a design this
 * produces is always saveable.
 */
import type { WidgetThemeIntent } from "./schema";
import type { WidgetTheme } from "./theme-fields";

const RADIUS: Record<WidgetThemeIntent["radius"], { launcher: string; chat: string }> = {
  sharp: { launcher: "8px", chat: "6px" },
  rounded: { launcher: "16px", chat: "14px" },
  pill: { launcher: "9999px", chat: "18px" },
};

const SHADOW: Record<WidgetThemeIntent["elevation"], string> = {
  flat: "rgba(0, 0, 0, 0.12)",
  soft: "rgba(0, 0, 0, 0.22)",
  strong: "rgba(0, 0, 0, 0.34)",
};

/**
 * `--bhjs-*` suffix → value. Only emits keys the design actually sets, so the
 * widget keeps its own defaults for everything else.
 *
 * Returns WidgetTheme rather than an open Record<string, string>, so emitting a
 * suffix outside the accepted allow-list is a COMPILE error. It previously
 * returned the open type and emitted `accent`, which nothing reads
 * (`--bhjs-accent` appears nowhere) and which the write schema rejected — so
 * every "Apply design" press produced a theme the server refused.
 */
export function themeIntentToBhjs(t: WidgetThemeIntent): WidgetTheme {
  return {
    background: t.primary,
    color: t.onPrimary,
    "background-hover": t.primaryHover,
    shadow: SHADOW[t.elevation],
    radius: RADIUS[t.radius].launcher,
    "chat-radius": RADIUS[t.radius].chat,
    "banner-bg": t.primary,
    "banner-fg": t.onPrimary,
    "banner-btn-bg": t.accent ?? t.primaryHover,
    "banner-btn-fg": t.onPrimary,
    // Entry-point bars / chips (hero stack) — LLM palette for surface + text.
    surface: t.surface,
    "on-surface": t.onSurface,
  };
}

/**
 * Bar geometry, read back OUT of the mapped `--bhjs-*` values so the runtime
 * and the dashboard preview cannot disagree about what a theme looks like.
 *
 * `50%` is the round launcher bubble's radius and is meaningless on a bar
 * (it would make an ellipse), so it falls back. `9999px` is NOT rejected: on a
 * 58px-tall bar that is a pill, which is exactly what the tenant asked for by
 * picking Pill — the previous rule threw it away and made that option inert.
 */
export function barRadius(theme: Record<string, string>): string {
  const r = theme["radius"];
  return r && r !== "50%" ? r : "14px";
}

/** Shadow colour for bar chrome — the depth the tenant chose (flat/soft/strong
 *  differ only in alpha, so the same blur reads as three real weights). */
export function barShadow(theme: Record<string, string>): string {
  return theme["shadow"] ?? "rgba(15, 23, 42, 0.18)";
}
